import React, { useState, useRef, useEffect } from 'react';
import {
  Autocomplete,
  FormControl,
  Checkbox,
  TextField,
  Box,
  Paper,
  Divider,
  Chip,
  Tooltip,
  IconButton,
} from '@mui/material';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CloseIcon from '@mui/icons-material/Close';
import { ButtonV2 } from 'components/common/ui';
import { generateShortId } from '@utils';
import styles from './filterMultiSelect.style';

const FilterMultiSelect = ({
  value,
  onChange,
  loading = false,
  label = '',
  options,
  id = 'multiselect',
  hasSelectAllOption = false,
  minWidth,
  align,
  ...props
}) => {
  const { disablePortal = true, limitTags = 1 } = props;

  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [isLargeScreen, setIsLargeScreen] = useState(false);
  const tooltipRef = useRef(null);
  const tooltipContentRef = useRef(null);

  // Check screen width
  useEffect(() => {
    const checkScreenSize = () => {
      setIsLargeScreen(window.innerWidth >= 1920);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);

    return () => {
      window.removeEventListener('resize', checkScreenSize);
    };
  }, []);

  // Handle click outside to close tooltip
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Don't close if clicking on the tooltip trigger or tooltip content
      if (
        (tooltipRef.current && tooltipRef.current.contains(event.target)) ||
        (tooltipContentRef.current &&
          tooltipContentRef.current.contains(event.target))
      ) {
        return;
      }
      setTooltipOpen(false);
    };

    if (tooltipOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [tooltipOpen]);

  const _handleOnChange = (_event, newValue) => {
    if (onChange) {
      onChange(newValue);
    }
  };

  const icon = (
    <CheckBoxOutlineBlankIcon
      data-testid="CheckBoxOutlineBlankIcon"
      fontSize="small"
    />
  );
  const checkedIcon = (
    <CheckBoxIcon data-testid="CheckBoxIcon" fontSize="small" />
  );
  const hasValue = !!value.length;

  const renderPaper = ({ children }) => {
    return (
      <Paper sx={styles.paper} onMouseDown={(e) => e.preventDefault()}>
        {!hasSelectAllOption ? (
          <>
            {value.length > 0 && (
              <>
                <Box
                  onMouseDown={(e) => e.preventDefault()} // prevent blur
                  sx={styles.headerWrapper}
                >
                  <Box sx={{ flex: 1 }} />
                  <ButtonV2
                    variant="text"
                    size="small"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      _handleOnChange(null, []);
                    }}
                    sx={styles.resetButton}
                  >
                    Reset
                  </ButtonV2>
                </Box>
                <Divider />
              </>
            )}
            {children}
          </>
        ) : (
          <>
            <Box
              onMouseDown={(e) => e.preventDefault()} // prevent blur
              sx={styles.headerWrapper}
            >
              <ButtonV2
                variant="text"
                size="small"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onChange(value.length === options.length ? [] : options);
                }}
                sx={styles.selectAllButton}
              >
                {value.length === options.length
                  ? 'Deselect all'
                  : 'Select all'}
              </ButtonV2>
              {value.length > 0 && (
                <ButtonV2
                  variant="text"
                  size="small"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    _handleOnChange(null, []);
                  }}
                  sx={styles.resetButton}
                >
                  Reset
                </ButtonV2>
              )}
            </Box>
            <Divider />
            {children}
          </>
        )}
      </Paper>
    );
  };

  const renderOption = (autoCompleteProps, option, { selected }) => {
    const uniqueKey =
      option.key || option.value || option.text || generateShortId();
    return (
      <Box
        component="li"
        {...autoCompleteProps}
        key={uniqueKey}
        sx={styles.filterOptionItem}
      >
        <Checkbox
          key={`checkbox-${uniqueKey}`}
          size="small"
          icon={icon}
          checkedIcon={checkedIcon}
          sx={styles.checkbox}
          checked={selected}
        />
        {option.text || ''}
      </Box>
    );
  };
  const renderInput = (params) => {
    return (
      <TextField
        variant="outlined"
        label={label}
        sx={{
          ...styles.textfield,
          ...(hasValue && !props.disabled && styles.textfieldWithValue),
        }}
        data-testid={`input-${id}`}
        {...params}
      />
    );
  };

  const renderTags = (tagValue, getTagProps) => {
    const more = tagValue.length - limitTags;
    const displayTags = tagValue
      ?.slice(0, limitTags)
      .filter((option) => option);

    const handleRemoveTag = (tagToRemove) => {
      const removeValue =
        typeof tagToRemove === 'string' ? tagToRemove : tagToRemove?.value;
      const newValue = tagValue?.filter((tag) => {
        const tagVal = typeof tag === 'string' ? tag : tag?.value;
        return tagVal !== removeValue;
      });
      _handleOnChange(null, newValue);
    };
    return (
      <Box sx={styles.tooltipContainer}>
        {displayTags.map((option, index) => {
          if (option) {
            return (
              <Tooltip
                key={option.value || index}
                title={option.text}
                arrow
                placement="top"
                disableHoverListener={!option.text || option.text.length <= 6}
              >
                <Chip
                  {...getTagProps({ index })}
                  label={option.text}
                  size="small"
                  sx={styles.displayTag(isLargeScreen)}
                />
              </Tooltip>
            );
          }
          return null;
        })}
        {more > 0 && (
          <Tooltip
            open={tooltipOpen}
            onClose={() => setTooltipOpen(false)}
            disableHoverListener
            disableTouchListener
            title={
              <Box ref={tooltipContentRef} sx={styles.tooltipContent}>
                <IconButton
                  size="small"
                  onClick={() => setTooltipOpen(false)}
                  sx={styles.tooltipCloseButton}
                >
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
                <Box sx={styles.tooltipChipsWrapper}>
                  {tagValue.slice(limitTags).map((option, index) => {
                    if (option) {
                      return (
                        <Chip
                          key={option.value || index}
                          label={option.text}
                          size="small"
                          sx={styles.tooltipChip}
                          disabled={props.disabled}
                          onDelete={
                            !props.disabled
                              ? () => handleRemoveTag(option)
                              : undefined
                          }
                        />
                      );
                    }
                    return null;
                  })}
                </Box>
              </Box>
            }
            arrow
            placement="bottom-start"
            componentsProps={{
              tooltip: {
                sx: styles.tooltip,
              },
            }}
          >
            <Chip
              ref={tooltipRef}
              label={`+${more}`}
              size="small"
              variant="outlined"
              onClick={() => setTooltipOpen(!tooltipOpen)}
              sx={styles.moreChip}
            />
          </Tooltip>
        )}
      </Box>
    );
  };

  return (
    <FormControl
      variant="outlined"
      sx={{ m: 0, width: props?.width || '100%' }}
    >
      <Box sx={styles.wrapper({ minWidth, align })}>
        <Autocomplete
          multiple
          disablePortal={disablePortal}
          disableCloseOnSelect
          sx={styles.autocomplete}
          disabled={loading}
          value={value || []}
          onChange={_handleOnChange}
          loading={loading}
          ListboxProps={{ style: { maxHeight: 300 } }}
          isOptionEqualToValue={(option, newValue) => {
            return option.value === newValue?.value;
          }}
          getOptionLabel={(option) => {
            if (typeof option === 'string' || option instanceof String) {
              return option;
            }
            return option?.text || option?.label;
          }}
          options={options}
          PaperComponent={renderPaper}
          renderOption={renderOption}
          renderInput={renderInput}
          renderTags={renderTags}
          size="small"
          {...props}
        />
      </Box>
    </FormControl>
  );
};

export default FilterMultiSelect;
